import { documentService } from '@/services/document.service'
import { useAuthStore } from '@/stores/auth.store'
import type { AddLearnedSkillsRequest, Document, JobMatchingStatus } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/** Keep in sync with services `STALE_JOB_MATCHING_MINUTES` (document_service.py). */
export const RESUME_SCAN_AGENT_STALE_THRESHOLD_MS = 10 * 60 * 1000

type ResumeScanAgentDocument = Pick<
  Document,
  'job_matching_status' | 'job_matching_started_at' | 'status' | 'updated_at'
>

function isActiveMatchingStatus(status: Document['job_matching_status']): boolean {
  return status === 'pending' || status === 'matching'
}

function elapsedMsSince(iso: string | null | undefined): number | null {
  if (!iso) return null
  const elapsed = Date.now() - new Date(iso).getTime()
  return Number.isFinite(elapsed) ? elapsed : null
}

/** True when matching started but stalled, or resume is ready and queued matching never started. */
export function isResumeScanAgentStale(doc: ResumeScanAgentDocument): boolean {
  if (!isActiveMatchingStatus(doc.job_matching_status)) return false

  const startedElapsed = elapsedMsSince(doc.job_matching_started_at)
  if (startedElapsed !== null && startedElapsed > RESUME_SCAN_AGENT_STALE_THRESHOLD_MS) {
    return true
  }

  if (!doc.job_matching_started_at && doc.status === 'ready') {
    const updatedElapsed = elapsedMsSince(doc.updated_at)
    if (updatedElapsed !== null && updatedElapsed > RESUME_SCAN_AGENT_STALE_THRESHOLD_MS) {
      return true
    }
  }

  return false
}

export function isResumeScanAgentActive(doc: ResumeScanAgentDocument): boolean {
  if (doc.job_matching_status === 'failed') return false
  if (isResumeScanAgentStale(doc)) return false
  return isActiveMatchingStatus(doc.job_matching_status)
}

export function isResumeScanAgentCompleted(doc: ResumeScanAgentDocument): boolean {
  return doc.job_matching_status === 'completed'
}

export function isResumeScanAgentFailed(doc: ResumeScanAgentDocument): boolean {
  return doc.job_matching_status === 'failed' || isResumeScanAgentStale(doc)
}

/** Maps API rows to the status the UI should show (includes client-side stale as failed). */
export function getResumeScanAgentDisplayStatus(
  doc: ResumeScanAgentDocument,
): JobMatchingStatus | null {
  if (isResumeScanAgentCompleted(doc)) return 'completed'
  if (isResumeScanAgentFailed(doc)) return 'failed'
  if (isResumeScanAgentActive(doc)) {
    return doc.job_matching_status === 'matching' ? 'matching' : 'pending'
  }
  return doc.job_matching_status ?? null
}

export const documentKeys = {
  all: ['documents'] as const,
  list: () => [...documentKeys.all, 'list'] as const,
}

/**
 * Centralized hook for fetching documents.
 * Uses staleTime to prevent excessive refetches across components.
 * Multiple components can call this - React Query will share the cached data.
 */
export function useDocuments() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  return useQuery({
    queryKey: documentKeys.list(),
    queryFn: () => documentService.listDocuments(),
    enabled: isAuthenticated,
    // Data is considered fresh for 30 seconds - prevents refetch on mount
    staleTime: 30 * 1000,
    // Keep in cache for 5 minutes after unmount
    gcTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const documents = query.state.data
      if (
        !documents?.some((doc) => doc.document_type === 'resume' && isResumeScanAgentActive(doc))
      ) {
        return false
      }
      return 30 * 1000
    },
  })
}

/**
 * Get the current (most recent) resume from documents.
 */
export function useCurrentResume() {
  const { data: documents, ...rest } = useDocuments()

  const currentResume = documents
    ?.filter((d) => d.document_type === 'resume')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]

  return { currentResume, documents, ...rest }
}

export function useAddLearnedSkills(documentId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AddLearnedSkillsRequest) =>
      documentService.addLearnedSkills(documentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all })
    },
  })
}
